import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { TrendingUp, TrendingDown, Minus, Pause } from 'lucide-react';
import { adaptiveMacrosApi } from '../api/adaptiveMacros';

const AdaptiveMacroHistoryDialog = ({ clientId = null, open, onClose }) => {
  const { t } = useTranslation();
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await adaptiveMacrosApi.listAdjustments(clientId, 100);
        if (alive) setAdjustments(data?.adjustments || []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, clientId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('adaptiveMacroCard.historyTitle')}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : adjustments.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {t('adaptiveMacroCard.historyEmpty')}
          </div>
        ) : (
          <div className="space-y-3">
            {adjustments.map((a) => {
              const delta = (a.new_targets?.kcal || 0) - (a.previous_targets?.kcal || 0);
              const Icon = a.decision === 'pause_insufficient_data'
                ? Pause
                : delta > 0 ? TrendingUp
                : delta < 0 ? TrendingDown
                : Minus;
              const color = delta > 0 ? 'text-green-500'
                : delta < 0 ? 'text-red-500'
                : 'text-muted-foreground';
              return (
                <div
                  key={a.id}
                  className="rounded-lg border border-border/60 p-3 space-y-1.5"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className={`h-4 w-4 shrink-0 ${color}`} />
                      <span className="text-sm font-medium truncate">
                        {a.decision === 'hold' ? t('adaptiveMacroCard.statusHeld') :
                          a.decision === 'pause_insufficient_data' ? t('adaptiveMacroCard.statusPaused') :
                          `${delta > 0 ? '+' : ''}${delta} kcal`}
                      </span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 shrink-0">
                        {a.decision}
                      </Badge>
                    </div>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {new Date(a.adjusted_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{a.reason}</p>
                  <div className="flex gap-3 text-[11px] text-muted-foreground">
                    <span>{t('adaptiveMacroCard.trendLabel')} <span className="tabular-nums">{a.weight_trend_kg_per_week >= 0 ? '+' : ''}{a.weight_trend_kg_per_week?.toFixed?.(2) ?? '0.00'} kg/wk</span></span>
                    {a.adherence_pct != null && <span>{t('adaptiveMacroCard.adherenceLabel')} {a.adherence_pct}%</span>}
                  </div>
                  <div className="flex gap-2 text-[11px] pt-1">
                    <span className="text-muted-foreground">
                      {a.previous_targets?.kcal ?? '—'} → <span className="font-semibold text-foreground tabular-nums">{a.new_targets?.kcal ?? '—'}</span> kcal
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AdaptiveMacroHistoryDialog;
